'use client'

import { type FC, type PropsWithChildren, useContext } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { FormattedMessage } from 'react-intl'

import { useHookFormFriendlyCaptchaFieldControls } from '../../../friendly-captcha/components/FriendlyCaptchaChallenge/useHookFormFriendlyCaptchaFieldControls'
import { useGlobalConfigContext } from '../../../global-config/context/globalConfigContext'
import {
  LOGIN_OTP_STATE,
  LoginRegistrationUserLoginStateContext,
} from '../../../login-registration/context/LoginRegistrationUserLoginStateContext'
import { type LoginAfterLoggedIn } from '../../../login-registration/loginAfterLoggedIn'
import { urlResolverGetForgotPassword } from '../../../url-handling/urlResolver'
import { useTwoStepLoginPasswordTracking } from '../twoStepLoginPasswordTracking'

import { LoginRegistrationUserPasswordForm } from './LoginRegistrationUserPasswordForm'
import {
  type PasswordLoginFormData,
  useLoginRegistrationUserPasswordLoginSubmission,
} from './useLoginRegistrationUserPasswordLoginSubmission'

interface LoginRegistrationUserPasswordLoginFormProps {
  afterLoggedIn?: LoginAfterLoggedIn
}

export const LoginRegistrationUserPasswordLoginForm: FC<
  PropsWithChildren<LoginRegistrationUserPasswordLoginFormProps>
> = ({ afterLoggedIn, children }) => {
  const globalConfig = useGlobalConfigContext()
  const { setLoginOtpState, username } = useContext(
    LoginRegistrationUserLoginStateContext,
  )
  const tracking = useTwoStepLoginPasswordTracking()
  const formMethods = useForm<PasswordLoginFormData>({
    defaultValues: {
      fcSolution: '',
      password: '',
    },
    mode: 'onBlur',
    reValidateMode: 'onChange',
  })
  const { register, setValue, watch } = formMethods
  const { onCaptchaDone, watchFriendlyCaptchaChange } =
    useHookFormFriendlyCaptchaFieldControls({
      fieldName: 'fcSolution',
      register,
      setValue,
      watch,
    })
  const submission = useLoginRegistrationUserPasswordLoginSubmission({
    afterLoggedIn,
    formMethods,
    setLoginOtpState,
    tracking,
    username,
  })

  return (
    <>
      <h2 className="mb-6 text-l font-medium">
        <FormattedMessage id="login.loginBox.headLine" />
      </h2>
      <FormProvider {...formMethods}>
        <LoginRegistrationUserPasswordForm
          captchaSolution={watchFriendlyCaptchaChange}
          forgotPasswordUrl={urlResolverGetForgotPassword(globalConfig)}
          onCaptchaDone={onCaptchaDone}
          onChangeEmail={() => {
            setLoginOtpState(LOGIN_OTP_STATE.LOGIN_REGISTER)
          }}
          submission={submission}
        >
          {children}
        </LoginRegistrationUserPasswordForm>
      </FormProvider>
    </>
  )
}
